import { API_ENDPOINTS } from '../config/api';
import { fetchWithAuth } from '../utils/authUtils';

/**
 * Servicio para manejar operaciones de documentos con el backend
 *
 * Este servicio interactúa con el backend Spring Boot que a su vez
 * se comunica con AWS S3 para almacenar los archivos.
 */

export interface Document {
  id: number;
  userId: number;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  contentType: string;
  documentType: string;
  s3Key: string;
  s3Url: string;
  status: 'pendiente' | 'aprobado' | 'rechazado';
  year: number;
  month: number;
  version: number;
  uploadedAt: string;
  updatedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  comments?: string;
  description?: string;
}

export interface UploadDocumentResponse {
  success: boolean;
  message: string;
  document: Document;
}

export interface DocumentsListResponse {
  success: boolean;
  count: number;
  documents: Document[];
}

export interface DocumentUrlResponse {
  success: boolean;
  url: string;
  expiresIn: string;
}

export interface ErrorResponse {
  success: false;
  error: string;
}

/**
 * Sube un reporte de cliente al servidor (pendiente de aprobación)
 *
 * @param file - El archivo a subir
 * @param userId - ID del usuario
 * @param year - Año al que corresponde el reporte
 * @param month - Mes al que corresponde el reporte (1-12)
 * @returns Información del documento subido
 */
export const uploadClientReport = async (
  file: File,
  userId: number,
  year: number,
  month: number
): Promise<UploadDocumentResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('userId', userId.toString());
  formData.append('year', year.toString());
  formData.append('month', month.toString());

  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/client-report`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al subir reporte');
  }

  return response.json();
};

/**
 * Sube un documento al servidor (y a S3) - Método genérico
 *
 * @param file - El archivo a subir
 * @param userId - ID del usuario
 * @param documentType - Tipo de documento (factura, boleta, nota-credito, guia-despacho, otro)
 * @param year - Año al que corresponde el documento
 * @param month - Mes al que corresponde el documento (1-12)
 * @returns Información del documento subido
 */
export const uploadDocument = async (
  file: File,
  userId: number,
  documentType: string,
  year: number,
  month: number
): Promise<UploadDocumentResponse> => {
  // Crear FormData para enviar el archivo
  const formData = new FormData();
  formData.append('file', file);
  formData.append('userId', userId.toString());
  formData.append('documentType', documentType);
  formData.append('year', year.toString());
  formData.append('month', month.toString());

  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/upload`, {
    method: 'POST',
    body: formData,
    // No establecer Content-Type, el browser lo hace automáticamente con boundary para multipart
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al subir documento');
  }

  return response.json();
};

/**
 * Obtiene todos los documentos de un usuario
 */
export const getUserDocuments = async (userId: number): Promise<DocumentsListResponse> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/user/${userId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener documentos');
  }

  return response.json();
};

/**
 * Obtiene documentos de un usuario filtrados por tipo
 */
export const getDocumentsByType = async (
  userId: number,
  type: string
): Promise<DocumentsListResponse> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/user/${userId}/type/${type}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener documentos');
  }

  return response.json();
};

/**
 * Obtiene un documento específico
 */
export const getDocument = async (documentId: number): Promise<{ success: boolean; document: Document }> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/${documentId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener documento');
  }

  return response.json();
};

/**
 * Obtiene una URL pre-firmada para descargar un documento
 *
 * Esta es la forma recomendada para descargar archivos de S3.
 * La URL es temporal (válida por 15 minutos) y permite acceso directo a S3.
 */
export const getDocumentDownloadUrl = async (documentId: number): Promise<DocumentUrlResponse> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/${documentId}/url`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al generar URL de descarga');
  }

  return response.json();
};

/**
 * Descarga un documento usando la URL pre-firmada
 *
 * Esta función:
 * 1. Obtiene la URL pre-firmada del backend
 * 2. Abre el archivo en una nueva pestaña o inicia la descarga
 */
export const downloadDocument = async (documentId: number, fileName: string): Promise<void> => {
  try {
    // Obtener URL pre-firmada
    const urlResponse = await getDocumentDownloadUrl(documentId);

    // Crear un link temporal y hacer click en él
    const link = document.createElement('a');
    link.href = urlResponse.url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error al descargar documento:', error);
    throw error;
  }
};

/**
 * Visualiza un documento en una nueva pestaña
 */
export const viewDocument = async (documentId: number): Promise<void> => {
  try {
    // Obtener URL pre-firmada
    const urlResponse = await getDocumentDownloadUrl(documentId);

    // Abrir en nueva pestaña
    window.open(urlResponse.url, '_blank');
  } catch (error) {
    console.error('Error al visualizar documento:', error);
    throw error;
  }
};

/**
 * Elimina un documento
 */
export const deleteDocument = async (documentId: number): Promise<{ success: boolean; message: string }> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/${documentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al eliminar documento');
  }

  return response.json();
};

/**
 * Actualiza el estado de un documento
 */
export const updateDocumentStatus = async (
  documentId: number,
  status: 'pendiente' | 'aprobado' | 'rechazado',
  comments?: string
): Promise<{ success: boolean; message: string; document: Document }> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/${documentId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, ...(comments !== undefined && { comments }) }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al actualizar estado');
  }

  return response.json();
};

/**
 * Obtiene TODOS los reportes de clientes de todas las clínicas (admin)
 */
/**
 * Obtiene los reportes del usuario autenticado.
 * El userId lo resuelve el backend desde el JWT — no depende del frontend.
 * Usar en la vista de la clínica (UploadDocuments).
 */
export const getMyClientReports = async (): Promise<DocumentsListResponse> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/my-client-reports`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener reportes');
  }
  return response.json();
};
export const getAllClientReportsAdmin = async (): Promise<DocumentsListResponse> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/all-client-reports`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener reportes');
  }

  return response.json();
};

/**
 * Obtiene los reportes de clientes pendientes de revisión (admin)
 */
export const getPendingClientReports = async (): Promise<DocumentsListResponse> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/pending-reports`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al obtener reportes pendientes');
  }

  return response.json();
};

/**
 * Aprueba un reporte de cliente (admin)
 */
export const approveDocument = async (
  documentId: number,
  adminId: number,
  comments?: string
): Promise<{ success: boolean; message: string; document: Document }> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/${documentId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adminId, ...(comments ? { comments } : {}) }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al aprobar documento');
  }

  return response.json();
};

/**
 * Rechaza un reporte de cliente con motivo obligatorio (admin)
 */
export const rejectDocument = async (
  documentId: number,
  adminId: number,
  comments: string
): Promise<{ success: boolean; message: string; document: Document }> => {
  const response = await fetchWithAuth(`${API_ENDPOINTS.documents}/${documentId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ adminId, comments }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al rechazar documento');
  }

  return response.json();
};

/**
 * Formatea el tamaño del archivo
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Valida el tipo de archivo
 */
export const isValidFileType = (file: File): boolean => {
  const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  return validTypes.includes(file.type);
};

/**
 * Valida el tamaño del archivo (máximo 10MB)
 */
export const isValidFileSize = (file: File): boolean => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  return file.size <= maxSize;
};

/**
 * Obtiene el nombre del mes en español
 */
export const getMonthName = (month: number): string => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  return months[month - 1] || '';
};

/**
 * Formatea el periodo (año/mes) en texto legible
 */
export const formatPeriod = (year: number, month: number): string => {
  return `${getMonthName(month)} ${year}`;
};
